import type { OrganizationWorkingHours } from '@kanbu/schema';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/**
 * Converts a PascalCase string to a kebab-case string.
 *
 * @example
 * const str = 'MyVariableName';
 * const kebab = pascalToKebab(str);
 *
 * -> kebab will be 'my-variable-name'
 */
export function pascalToKebab(str: string) {
  return str.replaceAll(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

const NANO_ID_CHARS =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * Generates a random ID with 8 characters. In following format:
 * '2k4j3h7k', you can customize the length by passing a different number.
 */
export function nanoId(length = 8) {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += NANO_ID_CHARS[Math.floor(Math.random() * NANO_ID_CHARS.length)];
  }

  return result;
}

/**
 * Chunks an array into smaller arrays of a given size.
 */
export function chunkArray<T>(arr: T[], size: number): T[][] {
  return arr.reduce<T[][]>(
    (acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]),
    [],
  );
}

/**
 * Extracts the file extension from a URI. The output includes the
 * leading dot, lowercased, e.g. `.txt`, `.md`, etc.
 *
 * URL-aware: the extension is taken from the URL pathname, so query
 * strings and fragments don't leak into the result
 * (e.g. `https://host/file.pdf?token=abc` -> `.pdf`). Protocol-less
 * inputs (bare filenames, plain paths) fall back to the raw string.
 * Returns `null` when no extension can be determined.
 */
export function extractFileExtension(uri: string): string | null {
  let path = uri;

  try {
    path = new URL(uri).pathname;
  } catch {
    // Not a parseable URL (e.g. a bare filename) — use the raw string.
  }

  const lastSegment = path.split('/').pop();

  if (!lastSegment || !lastSegment.includes('.')) {
    return null;
  }

  const extension = lastSegment.split('.').pop();

  if (!extension) {
    return null;
  }

  return `.${extension.toLocaleLowerCase()}`;
}

/**
 * Converts http and https URLs to ws and wss URLs.
 * The path is preserved. It is usually used to generate
 * websocket endpoints from base API urls.
 */
export function getWsUrlFromHttpUrl(url: string) {
  const parsedUrl = new URL(url);

  // Make sure the url ends without a last slash
  return `${parsedUrl.protocol === 'https:' ? 'wss' : 'ws'}://${parsedUrl.host}${parsedUrl.pathname.replace(/\/$/, '')}`;
}

/**
 * Sorts an array of objects by the 'createdAt' property.
 */
export function sortMessagesChronologically<
  T extends { createdAt: string | Date },
>(a: T, b: T): number {
  const aDate =
    a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
  const bDate =
    b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);

  return aDate.getTime() - bDate.getTime();
}

/**
 * Checks if a time range is valid. The from and to string are
 * expected to be in ISO 8601 format.
 *
 * @example
 * const isValid = isTimeRangeValid({ from: '2024-03-15T10:00:00Z', to: '2024-03-15T12:00:00Z' });
 *
 */
export function isTimeRangeValid({ from, to }: { from: string; to: string }) {
  return formatWorkingHoursTime(from) < formatWorkingHoursTime(to);
}

/**
 * Calculate the percentile value from a sorted array of numbers.
 */
export function getPercentile(
  sortedValues: number[],
  percentile: number,
): number {
  if (sortedValues.length === 0) {
    return 0;
  }

  if (percentile <= 0) {
    return sortedValues[0];
  }

  if (percentile >= 100) {
    return sortedValues.at(-1) ?? 0;
  }

  // Calculate the index with possible decimal part
  const index = (percentile / 100) * (sortedValues.length - 1);

  // Get integer and decimal parts
  const lowerIndex = Math.floor(index);
  const fraction = index - lowerIndex;

  // If it's exactly at an index
  if (fraction === 0) {
    return sortedValues[lowerIndex];
  }

  // Interpolate between the two closest values
  const lowerValue = sortedValues[lowerIndex];
  const upperValue = sortedValues[lowerIndex + 1];

  return lowerValue + (upperValue - lowerValue) * fraction;
}

/**
 * Converts ArrayBuffer to base64 string for web environments.
 * This is the most efficient native way to handle the conversion.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCodePoint(bytes[i]);
  }

  return btoa(binary);
}

export const WORKING_HOURS_TIME_ZONE = 'Europe/Prague';

export function formatWorkingHoursTime(value: string | Date): string {
  return formatInTimeZone(value, WORKING_HOURS_TIME_ZONE, 'HH:mm');
}

export function workingHoursTimeToIso({
  time,
  date = new Date(),
}: {
  time: string;
  date?: Date;
}): string {
  const day = formatInTimeZone(date, WORKING_HOURS_TIME_ZONE, 'yyyy-MM-dd');

  return fromZonedTime(
    `${day}T${time}:00`,
    WORKING_HOURS_TIME_ZONE,
  ).toISOString();
}

function getWorkingHoursDay(
  workingHours: NonNullable<OrganizationWorkingHours>,
) {
  const day =
    Number(formatInTimeZone(new Date(), WORKING_HOURS_TIME_ZONE, 'i')) % 7;

  return workingHours[String(day) as '0' | '1' | '2' | '3' | '4' | '5' | '6'];
}

export function isWithinHours(workingHours: OrganizationWorkingHours) {
  if (!workingHours) {
    return false;
  }

  const day = getWorkingHoursDay(workingHours);

  if (!Array.isArray(day)) {
    return false;
  }

  const now = formatInTimeZone(
    new Date(),
    WORKING_HOURS_TIME_ZONE,
    'HH:mm:ss.SSS',
  );

  return day.some(
    hours =>
      now >= `${formatWorkingHoursTime(hours.from)}:00.000` &&
      now <= `${formatWorkingHoursTime(hours.to)}:00.000`,
  );
}

export type TodayWorkingHours = {
  closed: boolean;
  ranges?: { from: string; to: string }[];
};

/**
 * Today's open intervals from org working hours, as HH:mm ranges.
 * `null` when no schedule is configured at all.
 */
export function getTodayWorkingHours(
  workingHours: OrganizationWorkingHours,
): TodayWorkingHours | null {
  if (!workingHours) {
    return null;
  }

  const day = getWorkingHoursDay(workingHours);

  if (day === undefined) {
    return null;
  }

  if (day === false || !Array.isArray(day) || day.length === 0) {
    return { closed: true };
  }

  return {
    closed: false,
    ranges: day.map(interval => ({
      from: formatWorkingHoursTime(interval.from),
      to: formatWorkingHoursTime(interval.to),
    })),
  };
}

/**
 * True when at least one weekday has an open interval. Used to decide whether
 * the model should mention “usual availability” (only when a schedule exists).
 */
export function hasConfiguredWorkingHours(
  workingHours: OrganizationWorkingHours,
): boolean {
  if (!workingHours) {
    return false;
  }

  return Object.values(workingHours).some(
    day => Array.isArray(day) && day.length > 0,
  );
}

/**
 * Live-chat connect availability: true only while the clock is inside an open
 * interval for today. Missing, empty, or all-closed schedules are unavailable
 * — the connect button stays hidden until hours are set and open.
 */
export function isOperatorLiveChatAvailable(
  workingHours: OrganizationWorkingHours,
): boolean {
  return isWithinHours(workingHours);
}

/** * Converts the hex color to RGB.
 *
 * #000000 -> [0, 0, 0]
 */
export function hexToRgb(hex: string) {
  const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);

  if (!result || result.length !== 4) {
    throw 'Failed to parse hex';
  }

  return [
    Number.parseInt(result[1], 16),
    Number.parseInt(result[2], 16),
    Number.parseInt(result[3], 16),
  ];
}
